import type { Category, EventData } from '@/types';

/**
 * Replace category placeholders in text with actual resource names or category names
 * Format: {{CategoryName}}
 *
 * @param text - The text containing placeholders
 * @param categories - Array of all categories
 * @param eventData - The event data containing resource assignments
 * @returns Text with placeholders replaced
 */
export function replaceCategoryPlaceholders(
    text: string,
    categories: Category[],
    eventData?: EventData
): string {
    if (!text) return text;

    // Find all placeholders in format {{CategoryName}}
    const placeholderRegex = /{{([^}]+)}}/g;

    return text.replace(placeholderRegex, (match, categoryName) => {
        const trimmedName = categoryName.trim();

        // Find the category by name (case-insensitive)
        const category = categories.find(
            (cat) => cat.name.toLowerCase() === trimmedName.toLowerCase()
        );

        if (!category) {
            // Category not found, return the placeholder as-is
            return match;
        }

        // Find the assigned resource(s) for this category in the event data
        if (eventData?.categories && eventData.categories[category.id]) {
            const categoryData = eventData.categories[category.id];

            const assignedNames = categoryData?.users?.map((u) => u.name).filter(Boolean);
            if (assignedNames && assignedNames.length > 0) {
                return assignedNames.join(', ');
            }
        }

        // No resource assigned, return the category name
        return category.name;
    });
}

/**
 * Create a category placeholder string
 * @param categoryName - The category name
 * @returns Formatted placeholder string
 */
export function createCategoryPlaceholder(categoryName: string): string {
    return `{{${categoryName}}}`;
}

/**
 * Extract all category placeholders from text
 * @param text - The text to search
 * @returns Array of category names found in placeholders
 */
export function extractCategoryPlaceholders(text: string): string[] {
    if (!text) return [];

    const placeholderRegex = /{{([^}]+)}}/g;
    const matches = [];
    let match;

    while ((match = placeholderRegex.exec(text)) !== null) {
        matches.push(match[1].trim());
    }

    return matches;
}
